import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Alert, Button, Stack, Paper, Chip, CircularProgress, LinearProgress,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useSnackbar } from 'notistack';

import BypassForm, { LabOption } from './components/BypassForm';
import CsvDropzone from './components/CsvDropzone';
import MappingPreview from './components/MappingPreview';
import { parseMetadataFromCsv } from './csvMetadata';

import api from '../../services/api';
import {
  PreviewResponse, RowDecision,
  previewBypass, submitBypass
} from '../../services/bypassApi';

type Stage = 'upload' | 'preview' | 'submitting';

const Bypass: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [stage, setStage] = useState<Stage>('upload');
  const [labName, setLabName] = useState<string>('');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [decisions, setDecisions] = useState<Record<number, RowDecision>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [labs, setLabs] = useState<LabOption[]>([]);
  const [autoFilledLab, setAutoFilledLab] = useState(false);
  const [unmappedConfirmOpen, setUnmappedConfirmOpen] = useState(false);
  const [newParamsConfirmOpen, setNewParamsConfirmOpen] = useState(false);

  // Load the lab list (union of LabConfig.labs[] and VendorParameterMap distinct vendors)
  useEffect(() => {
    (async () => {
      try {
        const [cfgRes, vendorsRes] = await Promise.all([
          api.get('/lab-config').catch(() => ({ data: { data: { labs: [] } } })),
          api.get('/vendor-suggestions/master/vendors').catch(() => ({ data: { vendors: [] } }))
        ]);
        const configLabs: string[] = (cfgRes.data?.data?.labs || []).map((l: any) => l.name).filter(Boolean);
        const vendorNames: string[] = (vendorsRes.data?.vendors || []).map((v: any) => v.vendorName).filter(Boolean);
        const vendorSet = new Set(vendorNames.map(n => n.toLowerCase()));
        const merged: Record<string, LabOption> = {};
        for (const n of configLabs) {
          merged[n.toLowerCase()] = { name: n, hasVendorMappings: vendorSet.has(n.toLowerCase()), source: vendorSet.has(n.toLowerCase()) ? 'both' : 'config' };
        }
        for (const n of vendorNames) {
          const key = n.toLowerCase();
          if (merged[key]) { merged[key].source = 'both'; merged[key].hasVendorMappings = true; }
          else merged[key] = { name: n, hasVendorMappings: true, source: 'vendor' };
        }
        setLabs(Object.values(merged).sort((a, b) => a.name.localeCompare(b.name)));
      } catch {}
    })();
  }, []);

  const allDecided = useMemo(() => {
    if (!preview) return false;
    return preview.rows.every(r => !!r.match || !!decisions[r.rowIndex]);
  }, [preview, decisions]);

  const unmappedCount = useMemo(() => {
    if (!preview) return 0;
    return preview.rows.filter(r => !r.match && !decisions[r.rowIndex]).length;
  }, [preview, decisions]);

  const newParamsCount = useMemo(() => Object.values(decisions).filter(d => d.action === 'addNew').length, [decisions]);

  // When the user drops a CSV, auto-fill labName from the comment block.
  const handleCsvFile = async (file: File | null) => {
    setCsvFile(file);
    setAutoFilledLab(false);
    if (!file) return;
    try {
      const parsed = await parseMetadataFromCsv(file);
      if (parsed.labName && !labName) {
        setLabName(parsed.labName);
        setAutoFilledLab(true);
      }
    } catch {}
  };

  const handlePreview = async () => {
    if (!csvFile || !labName) return;
    setLoading(true); setError(null);
    try {
      const r = await previewBypass({ labName } as any, csvFile);
      setPreview(r);
      setDecisions({});
      setStage('preview');
      enqueueSnackbar(`Parsed ${r.summary.totalRows} rows — ${r.summary.matched} auto-mapped, ${r.summary.needsDecision} need a decision`, { variant: 'info' });
    } catch (e: any) {
      const errs = e?.response?.data?.errors;
      if (Array.isArray(errs)) setError(errs.map((er: any) => `Line ${er.line}: ${er.message}`).join('\n'));
      else setError(e?.response?.data?.error || e.message || 'Preview failed.');
    } finally { setLoading(false); }
  };

  const performSubmit = async () => {
    if (!preview) return;
    setStage('submitting'); setLoading(true); setError(null);
    try {
      const rowsWithDecisions = preview.rows.map(r => {
        if (r.match) {
          return { ...r, decision: {
            action: 'map' as const,
            masterId: r.match.masterId,
            vendorRowId: r.match.vendorRowId,
            standardizedName: r.match.standardizedName
          } };
        }
        // user-decided OR undefined (= unmapped); backend treats missing decision as unmapped
        return { ...r, decision: decisions[r.rowIndex] };
      });
      const res = await submitBypass({
        metadata: { labName } as any,
        rows: rowsWithDecisions,
        csvFileName: csvFile?.name || 'bypass.csv',
        csvFileSize: csvFile?.size || 0
      });
      enqueueSnackbar(`Report created. Open it on the Review page to set Order ID.`, { variant: 'success' });
      setTimeout(() => navigate(`/nurse/review/${res.reportId}`), 800);
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Submit failed.');
      setStage('preview');
    } finally { setLoading(false); }
  };

  // Submission flow:
  //   - if any rows are unmapped → confirm unmapped first
  //   - then if any rows added new master entries → confirm queue-for-approval
  //   - then submit
  const proceedAfterUnmappedConfirm = () => {
    setUnmappedConfirmOpen(false);
    if (newParamsCount > 0) { setNewParamsConfirmOpen(true); return; }
    performSubmit();
  };
  const handleSubmitClick = () => {
    if (!preview) return;
    if (unmappedCount > 0) { setUnmappedConfirmOpen(true); return; }
    if (newParamsCount > 0) { setNewParamsConfirmOpen(true); return; }
    performSubmit();
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <WarningAmberIcon color="warning" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>Bypass — Manual CSV ingest</Typography>
          <Typography variant="body2" color="text.secondary">
            Emergency fallback for reports where OCR fails. Same downstream flow as a normal report — you'll set the Order ID on the Review page after submitting.
          </Typography>
        </Box>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }} onClose={() => setError(null)}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {stage === 'upload' && (
        <>
          <CsvDropzone file={csvFile} onFile={handleCsvFile} disabled={loading} />
          {autoFilledLab && (
            <Alert severity="info" sx={{ mt: 2 }} onClose={() => setAutoFilledLab(false)}>
              Lab Name auto-filled from CSV header — edit below if needed.
            </Alert>
          )}
          <Box sx={{ mt: 2 }}>
            <BypassForm labName={labName} onLabNameChange={setLabName} labs={labs} disabled={loading} />
          </Box>
          <Stack direction="row" spacing={2} sx={{ mt: 3 }} justifyContent="flex-end">
            <Button onClick={() => navigate(-1)} disabled={loading}>Cancel</Button>
            <Button variant="contained" color="warning"
              disabled={!csvFile || !labName || loading}
              onClick={handlePreview}>
              {loading ? <CircularProgress size={22} /> : 'Validate & preview mapping'}
            </Button>
          </Stack>
        </>
      )}

      {stage === 'preview' && preview && (
        <>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box flexGrow={1}>
                <Typography variant="body2">
                  Lab <strong>{preview.vendorName}</strong> · <code>{csvFile?.name}</code>
                </Typography>
              </Box>
              <Chip label={`${preview.summary.matched} matched`} color="success" variant="outlined" />
              <Chip label={`${preview.summary.needsDecision} need decision`} color={preview.summary.needsDecision === 0 ? 'success' : 'warning'} />
            </Stack>
          </Paper>

          <MappingPreview
            rows={preview.rows}
            decisions={decisions}
            onDecisionChange={(idx, dec) => setDecisions(prev => ({ ...prev, [idx]: dec }))}
          />

          <Stack direction="row" spacing={2} sx={{ mt: 3 }} justifyContent="space-between" alignItems="center">
            <Button onClick={() => { setStage('upload'); setPreview(null); setDecisions({}); }} disabled={loading}>
              Back
            </Button>
            <Stack direction="row" spacing={2} alignItems="center">
              {unmappedCount > 0 && (
                <Chip label={`${unmappedCount} unmapped — you can resolve later on Review`} size="small" color="warning" variant="outlined" />
              )}
              <Button variant="contained" color="warning"
                disabled={loading || preview.rows.length === 0}
                onClick={handleSubmitClick}>
                Create report → go to Review
              </Button>
            </Stack>
          </Stack>
        </>
      )}

      {/* Unmapped-rows confirmation modal */}
      <Dialog open={unmappedConfirmOpen} onClose={() => setUnmappedConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Some rows are still unmapped</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>{unmappedCount}</strong> {unmappedCount === 1 ? 'parameter row is' : 'parameter rows are'} not yet mapped to a master entry.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            They'll be saved on the report as <strong>"Unmapped"</strong> — you can resolve them on the Review page using the same mapping UI as a normal report, or leave them as-is.
          </Typography>
          <Alert severity="info">
            The report still gets created with all {preview?.rows.length} rows present. Only the master link is missing on the unmapped ones.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnmappedConfirmOpen(false)}>Cancel — keep mapping</Button>
          <Button onClick={proceedAfterUnmappedConfirm} variant="contained" color="warning">
            Proceed with {unmappedCount} unmapped
          </Button>
        </DialogActions>
      </Dialog>

      {/* New-parameter additions confirmation modal (no PIN — admin role is the gate) */}
      <Dialog open={newParamsConfirmOpen} onClose={() => setNewParamsConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Queue {newParamsCount} new parameter{newParamsCount === 1 ? '' : 's'} for approval?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>{newParamsCount}</strong> {newParamsCount === 1 ? 'parameter' : 'parameters'} will be queued as a pending suggestion for an admin to approve before being added to the canonical Parameter Master.
          </Typography>
          <Alert severity="info">
            Until approval, those parameters will appear on the Review page with a <em>"pending master approval"</em> flag.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewParamsConfirmOpen(false)}>Cancel</Button>
          <Button onClick={() => { setNewParamsConfirmOpen(false); performSubmit(); }} variant="contained" color="warning">
            Queue & create report
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Bypass;
